import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCog } from "react-icons/fa";
import Modal from "./Modal";
import EditChangeSummary from "./EditChangeSummary";
import { CEP_LOOKUP_STATUS, lookupCep, normalizeCepDigits } from "../helpers/cepLookup";
import {
  ADDRESS_MAX_LENGTHS,
  BRAZILIAN_UFS,
  NO_HOUSE_NUMBER,
  buildAddressPayload,
  formatCep,
  maskCepInput,
  toAddressForm,
  validateAddressForm,
} from "../helpers/addressHelper";
import "../styles/AddressEditModal.css";

const SUMMARY_FIELDS = ["postalCode", "street", "houseNumber", "complement", "neighborhood", "city", "state"];
const FORM_FIELDS = ["postalCode", "street", "houseNumber", "complement", "neighborhood", "city", "state"];

const displayValue = (field, value) => {
  if (value == null || String(value).trim() === "") return "-";
  return field === "postalCode" ? formatCep(value) : String(value);
};

const AddressEditModal = ({ isOpen, onClose, currentAddress, onSave, t, lookup = lookupCep }) => {
  const [form, setForm] = useState(() => toAddressForm(currentAddress));
  const [touched, setTouched] = useState({});
  const [cepStatus, setCepStatus] = useState(null);
  const [lockedFields, setLockedFields] = useState({ city: false, state: false });
  const [step, setStep] = useState("form");
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);
  const [serverFieldErrors, setServerFieldErrors] = useState({});
  const numberRef = useRef(null);
  const complementRef = useRef(null);
  const lookupRef = useRef(null);
  const lastLookedUpCepRef = useRef(normalizeCepDigits(toAddressForm(currentAddress).postalCode));

  const abortLookup = useCallback(() => {
    lookupRef.current?.abort();
    lookupRef.current = null;
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    const initial = toAddressForm(currentAddress);
    setForm(initial);
    setTouched({});
    setCepStatus(null);
    setLockedFields({ city: false, state: false });
    setStep("form");
    setSaving(false);
    setServerError(null);
    setServerFieldErrors({});
    lastLookedUpCepRef.current = normalizeCepDigits(initial.postalCode);
    return abortLookup;
  }, [isOpen, currentAddress, abortLookup]);

  const errors = useMemo(() => validateAddressForm(form), [form]);
  const isValid = Object.keys(errors).length === 0;
  const isLookingUp = cepStatus === "loading";

  const hasMaskedCurrentValues = useMemo(
    () => SUMMARY_FIELDS.some((field) => typeof currentAddress?.[field] === "string" && currentAddress[field].includes("*")),
    [currentAddress],
  );

  const runLookup = useCallback(async (digits) => {
    abortLookup();
    const controller = new AbortController();
    lookupRef.current = controller;
    lastLookedUpCepRef.current = digits;
    setCepStatus("loading");

    let result;
    try {
      result = await lookup(digits, { signal: controller.signal });
    } catch (error) {
      if (error?.name === "AbortError") return;
      result = { status: CEP_LOOKUP_STATUS.UNAVAILABLE };
    }
    if (lookupRef.current !== controller) return;
    lookupRef.current = null;

    if (result.status !== CEP_LOOKUP_STATUS.FOUND) {
      setCepStatus(result.status);
      setLockedFields({ city: false, state: false });
      return;
    }

    const { address } = result;
    const cityLocked = Boolean(address.city);
    const stateLocked = BRAZILIAN_UFS.includes(address.state);
    setForm((prev) => ({
      ...prev,
      street: address.street || prev.street,
      neighborhood: address.neighborhood || prev.neighborhood,
      city: cityLocked ? address.city : prev.city,
      state: stateLocked ? address.state : prev.state,
    }));
    setLockedFields({ city: cityLocked, state: stateLocked });
    setCepStatus(CEP_LOOKUP_STATUS.FOUND);
    setTimeout(() => {
      (numberRef.current && !numberRef.current.disabled ? numberRef.current : complementRef.current)?.focus();
    }, 0);
  }, [abortLookup, lookup]);

  const handleCepChange = (value) => {
    const masked = maskCepInput(value);
    const digits = normalizeCepDigits(masked);
    setForm((prev) => ({ ...prev, postalCode: masked }));
    setServerFieldErrors((prev) => ({ ...prev, postalCode: undefined }));

    if (digits.length === 8) {
      if (digits !== lastLookedUpCepRef.current || cepStatus === null) runLookup(digits);
      return;
    }
    abortLookup();
    lastLookedUpCepRef.current = digits;
    setCepStatus(null);
    setLockedFields({ city: false, state: false });
  };

  const handleChange = (field) => (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, [field]: value }));
    setServerFieldErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleBlur = (field) => () => setTouched((prev) => ({ ...prev, [field]: true }));

  const handleNoNumberChange = (event) => {
    const { checked } = event.target;
    setForm((prev) => ({ ...prev, noNumber: checked, houseNumber: checked ? "" : prev.houseNumber }));
    setServerFieldErrors((prev) => ({ ...prev, houseNumber: undefined }));
  };

  const errorText = (field) => {
    if (serverFieldErrors[field]) return serverFieldErrors[field];
    if (!touched[field] || !errors[field]) return null;
    const key = errors[field];
    return t(`clientDetails.address.errors.${key}`).replace("{max}", ADDRESS_MAX_LENGTHS[field]);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    setTouched(FORM_FIELDS.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    if (!isValid || isLookingUp) return;
    setServerError(null);
    setStep("confirm");
  };

  const handleConfirm = async () => {
    setSaving(true);
    setServerError(null);
    try {
      await onSave(buildAddressPayload(form));
    } catch (error) {
      const fieldErrors = error?.fieldErrors && typeof error.fieldErrors === "object" ? error.fieldErrors : {};
      setServerFieldErrors(fieldErrors);
      setServerError(
        error?.message
          || (error?.status === 403 ? t("clientDetails.address.errorForbidden") : t("clientDetails.address.errorGeneric")),
      );
      setStep("form");
    } finally {
      setSaving(false);
    }
  };

  const cepMessage = (() => {
    switch (cepStatus) {
      case "loading":
        return { tone: "info", text: t("clientDetails.address.cep.loading") };
      case CEP_LOOKUP_STATUS.FOUND:
        return { tone: "success", text: t("clientDetails.address.cep.found") };
      case CEP_LOOKUP_STATUS.NOT_FOUND:
        return { tone: "error", text: t("clientDetails.address.cep.notFound") };
      case CEP_LOOKUP_STATUS.INVALID:
        return { tone: "error", text: t("clientDetails.address.cep.invalid") };
      case CEP_LOOKUP_STATUS.UNAVAILABLE:
        return { tone: "warning", text: t("clientDetails.address.cep.unavailable") };
      default:
        return null;
    }
  })();

  const label = (field, required) => (
    <>
      {t(`clientDetails.address.fields.${field}`)}
      {required && <span className="address-edit-required" aria-hidden="true"> *</span>}
    </>
  );

  const fieldError = (field) => {
    const text = errorText(field);
    return text ? (
      <span className="address-edit-field-error" id={`address-${field}-error`} role="alert">{text}</span>
    ) : null;
  };

  const inputProps = (field, required = true) => ({
    id: `address-${field}`,
    className: `portal-input address-edit-input${errorText(field) ? " input-error" : ""}`,
    value: form[field],
    onChange: handleChange(field),
    onBlur: handleBlur(field),
    maxLength: ADDRESS_MAX_LENGTHS[field],
    "aria-invalid": Boolean(errorText(field)),
    "aria-describedby": errorText(field) ? `address-${field}-error` : undefined,
    "aria-required": required,
    disabled: saving,
  });

  const after = buildAddressPayload(form);

  return (
    <Modal isOpen={isOpen} onClose={saving ? () => {} : onClose}>
      <div className="address-edit-modal" data-testid="address-edit-modal">
        <h3 className="address-edit-title">
          {step === "confirm" ? t("clientDetails.address.confirmTitle") : t("clientDetails.address.modalTitle")}
        </h3>

        {serverError && (
          <div className="address-edit-banner is-error" role="alert">{serverError}</div>
        )}

        {step === "form" && (
          <form className="address-edit-form" onSubmit={handleSubmit} noValidate>
            <p className="address-edit-hint address-edit-wide" data-testid="address-history-info">
              {t("clientDetails.address.historyInfo")}
            </p>
            {hasMaskedCurrentValues && (
              <p className="address-edit-hint">{t("clientDetails.address.maskedHint")}</p>
            )}

            <div className="address-edit-field address-edit-cep">
              <label htmlFor="address-postalCode">{label("postalCode", true)}</label>
              <div className="address-edit-cep-control">
                <input
                  {...inputProps("postalCode")}
                  maxLength={9}
                  inputMode="numeric"
                  autoComplete="postal-code"
                  placeholder="00000-000"
                  onChange={(event) => handleCepChange(event.target.value)}
                />
                {isLookingUp && (
                  <FaCog className="spinner-cog address-edit-spinner" aria-label={t("clientDetails.address.cep.loading")} />
                )}
              </div>
              {fieldError("postalCode")}
              {cepMessage && (
                <span className={`address-edit-cep-status is-${cepMessage.tone}`} role="status">
                  {cepMessage.text}
                </span>
              )}
            </div>

            <div className="address-edit-field address-edit-wide">
              <label htmlFor="address-street">{label("street", true)}</label>
              <input {...inputProps("street")} autoComplete="address-line1" />
              {fieldError("street")}
            </div>

            <div className="address-edit-field">
              <label htmlFor="address-houseNumber">{label("houseNumber", !form.noNumber)}</label>
              <div className="address-edit-number-control">
                <input
                  {...inputProps("houseNumber", !form.noNumber)}
                  ref={numberRef}
                  value={form.noNumber ? NO_HOUSE_NUMBER : form.houseNumber}
                  disabled={saving || form.noNumber}
                />
                <label className="address-edit-checkbox" htmlFor="address-noNumber">
                  <input
                    id="address-noNumber"
                    type="checkbox"
                    className="address-edit-checkbox-input"
                    checked={form.noNumber}
                    onChange={handleNoNumberChange}
                    disabled={saving}
                  />
                  {t("clientDetails.address.fields.noNumber")}
                </label>
              </div>
              {!form.noNumber && fieldError("houseNumber")}
            </div>

            <div className="address-edit-field">
              <label htmlFor="address-complement">{label("complement", false)}</label>
              <input {...inputProps("complement", false)} ref={complementRef} autoComplete="address-line2" />
              {fieldError("complement")}
            </div>

            <div className="address-edit-field address-edit-wide">
              <label htmlFor="address-neighborhood">{label("neighborhood", true)}</label>
              <input {...inputProps("neighborhood")} />
              {fieldError("neighborhood")}
            </div>

            <div className="address-edit-field">
              <label htmlFor="address-city">{label("city", true)}</label>
              <input
                {...inputProps("city")}
                readOnly={lockedFields.city}
                className={`${inputProps("city").className}${lockedFields.city ? " is-locked" : ""}`}
                autoComplete="address-level2"
              />
              {fieldError("city")}
            </div>

            <div className="address-edit-field">
              <label htmlFor="address-state">{label("state", true)}</label>
              {lockedFields.state ? (
                <input {...inputProps("state")} readOnly className={`${inputProps("state").className} is-locked`} />
              ) : (
                <select
                  {...inputProps("state")}
                  maxLength={undefined}
                  className={`portal-select address-edit-input address-edit-select${
                    form.state ? "" : " is-placeholder"
                  }${errorText("state") ? " input-error" : ""}`}
                >
                  <option value="">{t("clientDetails.address.selectState")}</option>
                  {BRAZILIAN_UFS.map((uf) => (
                    <option key={uf} value={uf}>{uf}</option>
                  ))}
                </select>
              )}
              {fieldError("state")}
            </div>

            <p className="address-edit-hint address-edit-wide">{t("clientDetails.address.requiredHint")}</p>

            <div className="address-edit-actions address-edit-wide">
              <button type="button" className="portal-secondary-button" onClick={onClose}>
                {t("clientDetails.address.cancel")}
              </button>
              <button type="submit" className="portal-button" disabled={!isValid || isLookingUp}>
                {t("clientDetails.address.save")}
              </button>
            </div>
          </form>
        )}

        {step === "confirm" && (
          <div className="address-edit-confirm">
            <p className="address-edit-hint" data-testid="address-history-info-confirm">
              {t("clientDetails.address.historyInfo")}
            </p>
            <EditChangeSummary
              beforeLabel={t("clientDetails.address.before")}
              afterLabel={t("clientDetails.address.after")}
              rows={SUMMARY_FIELDS.map((field) => ({
                key: field,
                label: t(`clientDetails.address.fields.${field}`),
                before: displayValue(field, currentAddress?.[field]),
                after: displayValue(field, after[field]),
              }))}
            />
            <div className="address-edit-actions">
              <button type="button" className="portal-secondary-button" onClick={() => setStep("form")} disabled={saving}>
                {t("clientDetails.address.back")}
              </button>
              <button type="button" className="portal-button" onClick={handleConfirm} disabled={saving}>
                {saving ? t("clientDetails.address.saving") : t("clientDetails.address.confirm")}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default AddressEditModal;
